import React from 'react';
import { Button, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { FilePdfOutlined, DownloadOutlined, DeleteOutlined, LinkOutlined, RollbackOutlined, EyeOutlined, WalletOutlined, DollarOutlined } from '@ant-design/icons';
import { TextService } from '../../../Services/TextService';
import { WebApiMemberDtoExternalPaymentDto, WebApiMemberDtoPaymentDto } from '../../../Repository/eventbookyapi';
import { formatDateString } from '../../../Helpers/DateHelpers';
import Table, { ColumnsType } from 'antd/lib/table';
import fortnoxIcon from '../../../Images/fortnox-icon.png';
import pingIcon from '../../../Images/pingpayments-icon.png';
import spirisIcon from '../../../Images/spiris-icon.png';
import { useAuth } from '../../../Contexts/AuthContext';

export type UnifiedPayment = {
    key: string;
    type: 'manual' | 'fortnox' | 'pingpayments' | 'spiris';
    paymentDate: Date | null | undefined;
    amount: number | null | undefined;
    year: string | null | undefined;
    status: 'paid' | 'unpaid' | 'pending' | 'refunded' | 'settled';
    original: WebApiMemberDtoExternalPaymentDto | WebApiMemberDtoPaymentDto;
    paymentLinkId?: string | null;
    payUrl?: string | null;
    invoiceUrl?: string | null;
    dueDate?: Date | null;
    invoiceDate?: Date | null;
    settledAt?: Date | null;
    createdAt?: Date;
    createdByName?: string | null;
    manualPaymentId?: number;
    refundDescription?: string | null;
};

interface PaymentTableProps {
    payments: UnifiedPayment[];
    onDeleteManual: (id: number) => void;
    onDeletePaymentLink: (payUrl: string) => void;
    onDownloadReceipt: (paymentLinkId: string) => void;
    onGenerateAndDownloadInvoice: (paymentLinkId: string) => void;
    onRefund: (payment: WebApiMemberDtoExternalPaymentDto) => void;
    receiptLoadingId: string | null;
    invoiceLoadingId: string | null;
}

const PaymentTable = (props: PaymentTableProps) => {
    const GetText = TextService.GetText;
    const { user } = useAuth();

    const getTypeIcon = (type: UnifiedPayment['type']) => {
        switch (type) {
            case 'fortnox':
                return <Tooltip title="Fortnox"><img src={fortnoxIcon} alt="Fortnox" style={{ width: 20, height: 20 }} /></Tooltip>;
            case 'pingpayments':
                return <Tooltip title="PingPayments"><img src={pingIcon} alt="PingPayments" style={{ width: 20, height: 20 }} /></Tooltip>;
            case 'spiris':
                return <Tooltip title="Spiris"><img src={spirisIcon} alt="Spiris" style={{ width: 20, height: 20 }} /></Tooltip>;
            case 'manual':
                return <Tooltip title={GetText("ManagePayments_ManualPayment")}><DollarOutlined style={{ fontSize: 18, color: '#666' }} /></Tooltip>;
        }
    };

    const getStatusTag = (status: UnifiedPayment['status']) => {
        switch (status) {
            case 'paid':
                return <Tag color="green">{GetText("ManagePayments_StatusPaid")}</Tag>;
            case 'unpaid':
                return <Tag color="red">{GetText("ManagePayments_StatusUnpaid")}</Tag>;
            case 'pending':
                return <Tag color="orange">{GetText("ManagePayments_StatusPending")}</Tag>;
            case 'refunded':
                return <Tag color="red">{GetText("ManagePayments_StatusRefunded")}</Tag>;
            case 'settled':
                return <Tag color="blue">{GetText("ManagePayments_StatusSettled")}</Tag>;
        }
    };

    const columns: ColumnsType<UnifiedPayment> = [
        {
            title: '',
            key: 'type',
            width: 40,
            render: (_, record) => getTypeIcon(record.type),
        },
        {
            title: GetText("ManagePayments_ColumnStatus"),
            key: 'status',
            width: 130,
            render: (_, record) => getStatusTag(record.status),
        },
        {
            title: GetText("ManagePayments_ColumnDate"),
            key: 'paymentDate',
            width: 110,
            render: (_, record) => record.paymentDate ? formatDateString(record.paymentDate) : '-',
        },
        {
            title: GetText("ManagePayments_ColumnAmount"),
            key: 'amount',
            width: 100,
            render: (_, record) => record.amount != null ? `${record.amount} kr` : '-',
        },
        {
            title: GetText("ManagePayments_ColumnYear"),
            key: 'year',
            width: 70,
            render: (_, record) => record.year || '-',
        },
        {
            title: '',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    {/* Payment link */}
                    {record.payUrl && record.status === 'pending' && (
                        <Tooltip title={GetText("ManagePayments_CopyLink")}>
                            <Button
                                size="small"
                                type="link"
                                icon={<LinkOutlined />}
                                onClick={() => {
                                    navigator.clipboard.writeText(record.payUrl!);
                                    import('antd').then(({ message }) => message.success(GetText("ManagePayments_LinkCopied")));
                                }}
                            />
                        </Tooltip>
                    )}
                    {/* Download receipt */}
                    {record.paymentLinkId && record.status === 'paid' && (
                        <Tooltip title={GetText("AddPaymentLink_DownloadReceipt")}>
                            <Button
                                size="small"
                                type="link"
                                icon={<FilePdfOutlined />}
                                loading={props.receiptLoadingId === record.paymentLinkId}
                                onClick={() => props.onDownloadReceipt(record.paymentLinkId!)}
                            />
                        </Tooltip>
                    )}
                    {/* Generate & download invoice */}
                    {record.paymentLinkId && record.type === 'pingpayments' && (
                        <Tooltip title={GetText("AddPaymentLink_DownloadInvoice")}>
                            <Button
                                size="small"
                                type="link"
                                icon={<DownloadOutlined />}
                                loading={props.invoiceLoadingId === record.paymentLinkId}
                                onClick={() => props.onGenerateAndDownloadInvoice(record.paymentLinkId!)}
                            />
                        </Tooltip>
                    )}
                    {/* Refund */}
                    {record.type === 'pingpayments' && record.status === 'paid' && !record.settledAt && record.paymentLinkId && (
                        <Tooltip title={GetText("Refund_Button")}>
                            <Button
                                size="small"
                                type="link"
                                icon={<RollbackOutlined />}
                                onClick={() => props.onRefund(record.original as WebApiMemberDtoExternalPaymentDto)}
                            />
                        </Tooltip>
                    )}
                    {/* Delete payment link */}
                    {record.type === 'pingpayments' && record.status === 'pending' && record.payUrl && (
                        <Popconfirm
                            title={GetText("AddPaymentLink_DeleteConfirmTitle")}
                            description={GetText("AddPaymentLink_DeleteWarning")}
                            onConfirm={() => props.onDeletePaymentLink(record.payUrl!)}
                            okText={GetText("Common_Yes")}
                            cancelText={GetText("Common_No")}
                            okButtonProps={{ danger: true }}
                        >
                            <Tooltip title={GetText("AddPaymentLink_DeleteButton")}>
                                {user?.hasOnlyReadRights ? (
                                    <Button size="small" type="link" danger icon={<DeleteOutlined />} disabled />
                                ) : (
                                    <Button size="small" type="link" danger icon={<DeleteOutlined />} />
                                )}
                            </Tooltip>
                        </Popconfirm>
                    )}
                    {/* Delete manual payment */}
                    {record.type === 'manual' && record.manualPaymentId && (
                        <Tooltip title={GetText("Common_Delete")}>
                            <Button
                                size="small"
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => props.onDeleteManual(record.manualPaymentId!)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Table
            dataSource={props.payments}
            columns={columns}
            size="small"
            rowKey="key"
            pagination={false}
            scroll={{ x: 'max-content' }}
            expandable={{
                expandedRowRender: (record) => (
                    <div style={{ padding: '4px 0' }}>
                        {record.payUrl && (
                            <div style={{ marginBottom: 4 }}>
                                <strong>{GetText("AddPaymentLink_LinkLabel")}:</strong>{' '}
                                <a href={record.payUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{record.payUrl}</a>
                            </div>
                        )}
                        {record.dueDate && (
                            <div style={{ marginBottom: 4 }}><strong>{GetText("ManagePayments_DueDate")}:</strong> {formatDateString(record.dueDate)}</div>
                        )}
                        {record.invoiceDate && (
                            <div style={{ marginBottom: 4 }}><strong>{GetText("ManagePayments_InvoiceDate")}:</strong> {formatDateString(record.invoiceDate)}</div>
                        )}
                        {record.settledAt && (
                            <div style={{ marginBottom: 4 }}><strong>{GetText("ManagePayments_SettledAt")}:</strong> {formatDateString(record.settledAt)}</div>
                        )}
                        {record.createdAt && (
                            <div style={{ marginBottom: 4 }}><strong>{GetText("ManagePayments_CreatedAt")}:</strong> {formatDateString(record.createdAt)}</div>
                        )}
                        {record.createdByName && (
                            <div style={{ marginBottom: 4 }}><strong>{GetText("ManagePayments_CreatedBy")}:</strong> {record.createdByName}</div>
                        )}
                        {record.status === 'refunded' && record.refundDescription && (
                            <div style={{ marginBottom: 4 }}><strong>{GetText("ManagePayments_RefundDescription")}:</strong> {record.refundDescription}</div>
                        )}
                    </div>
                ),
                rowExpandable: (record) => !!(record.payUrl || record.dueDate || record.invoiceDate || record.settledAt || record.createdAt || record.createdByName || (record.status === 'refunded' && record.refundDescription)),
            }}
            locale={{ emptyText: GetText("AddPayment_NoPayments") }}
        />
    );
};

export default PaymentTable;
